/**
 * UpgradeTier — in-terminal top-up UI. Crypto-only (USDT TRC-20).
 * Pulls the full 11-tier catalog from /api/tiers/catalog so a client can
 * jump straight from their current tier up to Diplomatic in one view.
 * The paid delta grows their SEED CAPITAL — it is NOT withdrawable.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Bitcoin, Clock, Layers, Lock, ShieldCheck, Zap } from "lucide-react";
import CryptoPayModal from "./CryptoPayModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DRILLS_BY_INDEX = (i) =>
  i < 2 ? "Starter" : i < 5 ? "Standard" : i < 8 ? "Priority" : "Signature";
const TOOLS_BY_INDEX = (i) =>
  i < 3 ? "Core" : i < 6 ? "Advanced" : i < 9 ? "Pro" : "Elite";

export default function UpgradeTier({ currentTier = 100, accessCode }) {
  const [catalog, setCatalog] = useState([]);
  const [cryptoTier, setCryptoTier] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/tiers/catalog`);
        setCatalog(Array.isArray(data?.tiers) ? data.tiers : []);
      } catch {
        setErr("Could not load the tier catalog.");
      }
    })();
  }, []);

  const upgrades = useMemo(() => {
    const current = Number(currentTier) || 0;
    return catalog.filter((t) => Number(t.price) > current);
  }, [catalog, currentTier]);

  const currentTierName = useMemo(() => {
    const match = catalog.find((t) => Number(t.price) === Number(currentTier));
    return match?.name || "your current tier";
  }, [catalog, currentTier]);

  return (
    <div className="border border-gold/20 bg-surface p-6" data-testid="upgrade-tier">
      <div>
        <p className="text-[10px] uppercase tracking-[0.3em] text-gold">Tier upgrade</p>
        <p className="mt-1 font-serif text-2xl font-light text-white">
          Jump to a <span className="text-gold">deeper book</span>
        </p>
        <p className="text-[10px] uppercase tracking-[0.2em] text-white/40">
          You&apos;re on {currentTierName} (${Number(currentTier).toLocaleString()}) — pay the delta in USDT (TRC-20)
        </p>
      </div>

      <div
        data-testid="upgrade-usdt-notice"
        className="mt-4 flex items-start gap-2 rounded-sm border border-gold/25 bg-gold/[0.04] px-4 py-3 text-[10px] leading-relaxed text-gold/85"
      >
        <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span>
          Upgrades are paid in <span className="font-semibold">USDT on the TRC-20 network</span>.
          The paid amount joins your <span className="font-semibold">seed capital</span> — it
          builds your equity but is <span className="font-semibold">not withdrawable</span>.
          Only profits generated from that point forward can be paid out.
        </span>
      </div>

      {err && (
        <p className="mt-4 rounded-sm border border-rose-500/40 bg-rose-500/10 px-4 py-3 text-xs text-rose-300" data-testid="upgrade-error">
          {err}
        </p>
      )}

      {catalog.length > 0 && upgrades.length === 0 ? (
        <p
          data-testid="upgrade-max-tier"
          className="mt-6 rounded-sm border border-gold/20 bg-gold/[0.04] px-4 py-6 text-center text-sm text-white/70"
        >
          You&apos;re at the top tier — {currentTierName}. Enjoy every perk.
        </p>
      ) : (
        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {upgrades.map((t) => {
            const amt = Number(t.price);
            const delta = Math.max(0, amt - Number(currentTier));
            const idx = catalog.findIndex((x) => x.slug === t.slug);
            return (
              <div
                key={t.slug}
                data-testid={`upgrade-card-${t.slug}`}
                className="relative flex flex-col justify-between overflow-hidden rounded-lg border border-white/10 bg-ink/40 p-5 transition-all duration-300 hover:-translate-y-1"
                style={{ boxShadow: `inset 0 -2px 0 0 ${t.theme_hex}` }}
              >
                <div>
                  <div className="flex items-center gap-2">
                    <span
                      className="h-2 w-2 rounded-full"
                      style={{ background: t.theme_hex, boxShadow: `0 0 10px ${t.accent_hex}` }}
                    />
                    <p
                      className="text-[10px] uppercase tracking-[0.28em]"
                      style={{ color: t.theme_hex }}
                    >
                      {t.name}
                    </p>
                  </div>
                  <p className="mt-2 font-serif text-4xl font-light text-white">
                    <span className="align-top text-lg text-gold">$</span>
                    {amt.toLocaleString()}
                  </p>
                  <p className="mt-1 text-[10px] uppercase tracking-[0.2em] text-gold/70">
                    USDT · TRC-20
                  </p>

                  <div className="mt-4 space-y-1.5 text-[11px] text-white/75">
                    <div className="flex items-center justify-between border-b border-white/5 pb-1">
                      <span className="flex items-center gap-1.5 text-white/50">
                        <Zap className="h-3 w-3 text-gold" /> Daily training yield
                      </span>
                      <span className="font-tabular text-gold">{t.daily_rate_pct}%</span>
                    </div>
                    <div className="flex items-center justify-between border-b border-white/5 pb-1">
                      <span className="flex items-center gap-1.5 text-white/50">
                        <Clock className="h-3 w-3 text-gold" /> Cycle duration
                      </span>
                      <span className="font-tabular text-white/90">{t.duration_days} days</span>
                    </div>
                    <div className="flex items-center justify-between border-b border-white/5 pb-1">
                      <span className="flex items-center gap-1.5 text-white/50">
                        <Layers className="h-3 w-3 text-gold" /> Simulated book
                      </span>
                      <span className="font-tabular text-white/90">Level {idx + 1}</span>
                    </div>
                    <div className="flex items-center justify-between border-b border-white/5 pb-1">
                      <span className="text-white/50">Training tools</span>
                      <span className="font-tabular text-white/90">{TOOLS_BY_INDEX(idx)}</span>
                    </div>
                    <div className="flex items-center justify-between pb-0.5">
                      <span className="text-white/50">Concierge drills</span>
                      <span className="font-tabular text-white/90">{DRILLS_BY_INDEX(idx)}</span>
                    </div>
                  </div>

                  <p className="mt-3 text-[11px] text-white/55">
                    You send <span className="text-gold">${delta.toLocaleString()} USDT</span> now — grows
                    your seed capital (not withdrawable).
                  </p>
                </div>

                <button
                  data-testid={`upgrade-crypto-btn-${t.slug}`}
                  onClick={() =>
                    setCryptoTier({
                      key: `access_${amt}`,
                      amount: amt,
                      name: t.name,
                    })
                  }
                  className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-gold px-4 py-2.5 text-[11px] uppercase tracking-[0.2em] text-ink transition-colors hover:bg-gold-light"
                >
                  <Bitcoin className="h-3.5 w-3.5" /> Pay in USDT
                </button>
              </div>
            );
          })}
        </div>
      )}

      <p className="mt-6 flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-white/40">
        <Lock className="h-3 w-3" /> Tronscan-verified · payout to your TRC-20 wallet
      </p>

      <CryptoPayModal
        open={!!cryptoTier}
        onClose={() => setCryptoTier(null)}
        lookupKey={cryptoTier?.key}
        targetAmount={cryptoTier?.amount}
        topupCode={accessCode}
        currentTier={currentTier}
      />
    </div>
  );
}
